// src/components/auth/AuthShell.jsx
//
// Shared visual shell for the pre-auth + verification flow. Wraps any
// auth-flow page in the canonical lime/pitch design system used by
// Login + Register + ForgotPassword:
//
//   - Pitch (#0d1f14) full-bleed background
//   - Subtle lime grid pattern + decorative corner ring
//   - Top header with the Scrim/ly wordmark + LanguageSwitch
//   - Centered card container (default max-w-[460px], #162b1e bg, white text)
//   - Optional LegalLinks footer below the card
//
// The pages it wraps (MagicLinkLogin, LinkCallback, VerifyEmail,
// VerifyEmailPage, VerifyEmailRequired, ActionHandler) all share these
// outer chrome elements; the only difference is the card body, which
// each page owns.
//
// Why a component instead of inlining: when we tweak the chrome later
// (e.g. swap the Scrim/ly wordmark for a logo SVG, change the grid
// density, drop the decorative ring on small screens), one edit lands
// everywhere. The 3 pages that still inline this pattern (Login,
// Register, ForgotPassword) can fold in later with zero behavioral
// risk — visual output is identical.

import { Link as RouterLink } from 'react-router-dom';
import LanguageSwitch from '@/components/LanguageSwitch';
import LegalLinks from '@/components/LegalLinks';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const BODY_FONT = "'Barlow', sans-serif";

export default function AuthShell({
  children,
  // Card width — tighten for status-only screens (verify links,
  // callback) by passing 'narrow', or leave default for forms.
  cardWidth = 'default', // 'narrow' | 'default' | 'wide'
  // Whether to render the legal footer (privacy / terms / SMS policy).
  // Status callbacks where the user is already signed in don't need it.
  showLegalFooter = true,
  // Allow callers to override the default card surface — e.g. a
  // success card with a lime tint. Most pages use the default.
  cardClassName = '',
}) {
  const widthClass =
    cardWidth === 'narrow' ? 'max-w-[400px]'
    : cardWidth === 'wide' ? 'max-w-[520px]'
    : 'max-w-[460px]';

  return (
    <div
      className="relative min-h-dvh overflow-hidden bg-[#0d1f14] text-white"
      style={{ fontFamily: BODY_FONT }}
    >
      {/* Subtle grid background — lime lines on pitch, very low opacity */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(181,240,61,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      {/* Decorative corner ring — top-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-[240px] -top-[240px] h-[700px] w-[700px] rounded-full border border-[#b5f03d]/10"
        style={{
          boxShadow:
            '0 0 0 80px rgba(181,240,61,0.04), 0 0 0 160px rgba(181,240,61,0.02)',
        }}
      />

      <div className="relative z-10 flex min-h-dvh flex-col">
        {/* Top bar */}
        <header className="flex items-center justify-between border-b border-[#b5f03d]/40 bg-[#0d1f14] px-5 py-3">
          <RouterLink
            to="/"
            className="text-[#b5f03d]"
            style={{
              fontFamily: HEAD_FONT,
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: '-0.5px',
            }}
            aria-label="Scrimly home"
          >
            Scrim<span className="text-white">ly</span>
          </RouterLink>
          <LanguageSwitch />
        </header>

        {/* Card */}
        <main className="flex flex-col flex-1 items-center justify-center px-5 py-10 sm:py-14">
          <div
            className={`w-full ${widthClass} rounded-2xl border border-white/10 bg-[#162b1e] p-8 sm:p-10 ${cardClassName}`}
          >
            {children}
          </div>

          {showLegalFooter && (
            <div className="mt-6 px-5">
              <LegalLinks variant="subtle" className="!text-white/40" />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/* ── Shared subcomponents the auth flow pages reuse ────────────────────────── */

/** Pill above the heading (e.g. "Reset password", "Verify email"). */
export function AuthPill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[#b5f03d]/30 bg-[#b5f03d]/10 px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.5px] text-[#b5f03d]">
      {children}
    </span>
  );
}

/** Big Barlow-Condensed display heading. Pass a `<span className="text-[#b5f03d]">…</span>`
    inside `children` to highlight a word in lime. */
export function AuthHeading({ children, size = 'default' }) {
  const fontSize =
    size === 'small' ? 'clamp(26px, 3.4vw, 36px)'
    : size === 'large' ? 'clamp(32px, 4vw, 44px)'
    : 'clamp(28px, 3.6vw, 40px)';
  return (
    <h1
      className="mt-5 uppercase leading-none text-white"
      style={{ fontFamily: HEAD_FONT, fontWeight: 900, fontSize, letterSpacing: '-0.5px' }}
    >
      {children}
    </h1>
  );
}

/** Subtitle paragraph below the heading. */
export function AuthSubtitle({ children }) {
  return (
    <p className="mt-3.5 text-[15.5px] leading-relaxed text-white/65">
      {children}
    </p>
  );
}

/** Primary CTA button — lime on pitch, full width. */
export function AuthButton({ children, type = 'button', disabled, busy, onClick, className = '', ...rest }) {
  return (
    <button
      type={type}
      disabled={disabled}
      aria-busy={busy || undefined}
      onClick={onClick}
      className={`inline-flex w-full min-h-[52px] items-center justify-center rounded-[10px] bg-[#b5f03d] px-6 py-4 uppercase tracking-[0.5px] text-[#0d1f14] transition-all hover:bg-[#94cc28] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(181,240,61,0.25)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none ${className}`}
      style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 18 }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Secondary outlined button — for tertiary actions like "Try a different email". */
export function AuthButtonSecondary({ children, type = 'button', onClick, className = '', ...rest }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`inline-flex w-full min-h-[48px] items-center justify-center rounded-[10px] border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 ${className}`}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Form input — dark bg, white text, lime focus ring. Use with a sibling label. */
export const authInputClass =
  'w-full min-h-[52px] rounded-[10px] border border-white/10 bg-white/5 px-4 py-3.5 text-[15px] text-white placeholder:text-white/30 transition-colors focus:border-[#b5f03d] focus:bg-white/[0.08] focus:outline-none disabled:opacity-60';

/** Field label — Barlow Condensed uppercase, slightly muted. */
export const authLabelClass =
  'text-[13px] font-bold uppercase tracking-[1.2px] text-white/80';

/** Status banner — `tone` is one of 'error' | 'success' | 'info' | 'warn'. */
export function AuthBanner({ tone = 'info', title, children, onClose }) {
  const palette =
    tone === 'error' ? {
      border: 'border-red-500/30',
      bg:     'bg-red-500/10',
      dot:    'bg-red-400',
      text:   'text-red-200',
    }
    : tone === 'warn' ? {
      border: 'border-amber-500/30',
      bg:     'bg-amber-500/10',
      dot:    'bg-amber-400',
      text:   'text-amber-200',
    }
    : tone === 'success' ? {
      border: 'border-[#b5f03d]/40',
      bg:     'bg-[#b5f03d]/10',
      dot:    'bg-[#b5f03d]',
      text:   'text-[#b5f03d]',
    }
    : {
      border: 'border-white/15',
      bg:     'bg-white/5',
      dot:    'bg-white/60',
      text:   'text-white/85',
    };

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mb-5 flex items-start gap-3 rounded-lg border ${palette.border} ${palette.bg} px-4 py-3 text-sm ${palette.text}`}
    >
      <span className={`mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${palette.dot}`} />
      <div className="flex-1">
        {title && (
          <p
            className="font-bold mb-1"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            {title}
          </p>
        )}
        <div>{children}</div>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-white/60 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/40"
          aria-label="Dismiss"
        >
          ×
        </button>
      )}
    </div>
  );
}
